import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import CheckboxComponent from 'common/components/checkbox'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import Label from 'common/components/label'
import Loader from 'common/components/loaders/Loader'
import SingleSelect from 'common/components/singleSelect'
import Textarea from 'common/components/textarea'
import Widget from 'common/components/Widget'
import WidgetHeader from 'common/components/WidgetHeader'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import {
  setInheritBrandContext,
  updateBrandDescription,
  updateBrandName,
  updateIndustry,
} from '../store/styleGuideSlice'

type Props = {
  className?: string
}

const IndustryWidget = ({ className }: Props) => {
  const dispatch = useAppDispatch()
  const { showBrandContextFields: isBrandContextFieldsVisible } = useFlags()
  const {
    isLoading,
    industries,
    accountBrandName,
    accountBrandDescription,
    inheritBrandContext,
    editedContent: { industry: selectedIndustry, brandName, brandDescription },
  } = useAppSelector((state) => state.styleGuide)

  return (
    <Loader isLoading={isLoading}>
      <Widget type="basic" className={cx('flex flex-col gap-4', className)}>
        <WidgetHeader
          title="Brand Context"
          subtitle="This will add more context to the content you generate."
        />
        <div>
          <Label>Industry</Label>
          <SingleSelect
            className="w-full sm:w-80"
            data-cy="industry-select"
            data-testid="industry-select"
            name="industry"
            placeholder="Select your industry"
            onChange={(value) =>
              value?.value && dispatch(updateIndustry(value.value))
            }
            isLoading={isLoading}
            menuPortalTarget={document.body}
            options={industries}
            value={selectedIndustry}
          />
        </div>
        {isBrandContextFieldsVisible && (
          <>
            <CheckboxComponent
              className="mt-4"
              isChecked={inheritBrandContext}
              label="Use Account level Brand Name and Brand Description"
              onChange={(checked) => dispatch(setInheritBrandContext(checked))}
            />
            <FormItem label="Brand name">
              <div className="w-80">
                <Input
                  aria-label="Brand name"
                  type="text"
                  variant="default"
                  placeholder="This is used as context in content generation."
                  value={inheritBrandContext ? accountBrandName : brandName}
                  disabled={inheritBrandContext}
                  onChange={(e) => dispatch(updateBrandName(e.target.value))}
                />
              </div>
            </FormItem>
            <FormItem label="Brand description">
              <div className="w-96">
                <Textarea
                  aria-label="Brand description"
                  placeholder="Provide a description of the business, 1-2 sentences. This is used as context in content generation."
                  value={
                    inheritBrandContext
                      ? accountBrandDescription
                      : brandDescription
                  }
                  disabled={inheritBrandContext}
                  rows={4}
                  onChange={(e) =>
                    dispatch(updateBrandDescription(e.target.value))
                  }
                />
              </div>
            </FormItem>
          </>
        )}
      </Widget>
    </Loader>
  )
}

export default IndustryWidget
